import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";

export const LEGAL_SOURCE_SETTINGS = "settings";
export const LEGAL_SOURCE_SIGNUP = "signup";
export const LEGAL_SOURCE_REACCEPT = "reaccept";
export const LEGAL_SOURCE_BLOCKED = "blocked";
export const LEGAL_SOURCE_ABOUT = "about";

const LEGAL_SOURCE_VALUES = new Set([
  LEGAL_SOURCE_SETTINGS,
  LEGAL_SOURCE_SIGNUP,
  LEGAL_SOURCE_REACCEPT,
  LEGAL_SOURCE_BLOCKED,
  LEGAL_SOURCE_ABOUT,
]);

const BACK_LABELS = {
  en: {
    [LEGAL_SOURCE_SETTINGS]: "Back to Settings",
    [LEGAL_SOURCE_SIGNUP]: "Back to Sign Up",
    [LEGAL_SOURCE_REACCEPT]: "Back to acceptance",
    [LEGAL_SOURCE_BLOCKED]: "Back to account decision",
    [LEGAL_SOURCE_ABOUT]: "Back to About",
  },
  ru: {
    [LEGAL_SOURCE_SETTINGS]: "Назад в Settings",
    [LEGAL_SOURCE_SIGNUP]: "Назад к Sign Up",
    [LEGAL_SOURCE_REACCEPT]: "Назад к принятию документов",
    [LEGAL_SOURCE_BLOCKED]: "Назад к решению по аккаунту",
    [LEGAL_SOURCE_ABOUT]: "Назад к разделу «О нас»",
  },
  sk: {
    [LEGAL_SOURCE_SETTINGS]: "Späť do Settings",
    [LEGAL_SOURCE_SIGNUP]: "Späť na Sign Up",
    [LEGAL_SOURCE_REACCEPT]: "Späť na prijatie dokumentov",
    [LEGAL_SOURCE_BLOCKED]: "Späť k rozhodnutiu o účte",
    [LEGAL_SOURCE_ABOUT]: "Späť na O nás",
  },
};

const LEGAL_BACKGROUND_STYLE = {
  ...AUTH_GRADIENT_STYLE,
  backgroundColor: "#050812",
};

const normalizeLegalSource = (value, fallback = LEGAL_SOURCE_SIGNUP) => {
  const normalized = String(value || "").trim().toLowerCase();
  return LEGAL_SOURCE_VALUES.has(normalized) ? normalized : fallback;
};

const getLanguageKey = (language) => {
  const code = String(language || "").toLowerCase();
  if (code.startsWith("ru")) return "ru";
  if (code.startsWith("sk") || code.startsWith("cs")) return "sk";
  return "en";
};

export const getLegalPath = (pathname, source = LEGAL_SOURCE_SIGNUP) => {
  const params = new URLSearchParams();
  params.set("from", normalizeLegalSource(source));
  return `${pathname}?${params.toString()}`;
};

export const getLegalSourceFromLocation = (location, fallback = LEGAL_SOURCE_SIGNUP) => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeLegalSource(location?.state?.from || params.get("from"), fallback);
};

export const useLegalSource = (fallback = LEGAL_SOURCE_SIGNUP) => {
  const location = useLocation();
  return useMemo(() => getLegalSourceFromLocation(location, fallback), [fallback, location]);
};

const getBackTarget = (source) => {
  if (source === LEGAL_SOURCE_SETTINGS) return "/vault/settings";
  if (source === LEGAL_SOURCE_ABOUT) return "/about?tab=about";
  if (source === LEGAL_SOURCE_REACCEPT || source === LEGAL_SOURCE_BLOCKED) return "/";
  return "/auth?tab=register";
};

export default function LegalPageShell({ children }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const source = useLegalSource();
  const languageKey = getLanguageKey(i18n.language);
  const label = BACK_LABELS[languageKey]?.[source] || BACK_LABELS.en[source];

  const handleBack = () => {
    navigate(getBackTarget(source));
  };

  return (
    <div
      className="relative h-full min-h-[100dvh] overflow-hidden text-white lg:min-h-full"
      style={LEGAL_BACKGROUND_STYLE}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(5,8,18,0.08)_0%,rgba(5,8,18,0.42)_58%,rgba(5,8,18,0.72)_100%)]"
        aria-hidden="true"
      />
      <main className="relative z-10 h-full overflow-y-auto overscroll-contain px-6 pb-[calc(108px+env(safe-area-inset-bottom))] pt-[calc(max(env(safe-area-inset-top),0px)+40px)]">
        <article className="mx-auto w-full max-w-3xl text-sm leading-relaxed text-white md:text-base">
          {children}
        </article>
      </main>
      <div className="pointer-events-none absolute inset-x-0 bottom-[calc(max(env(safe-area-inset-bottom),0px)+22px)] z-20 flex justify-center px-6">
        <button
          type="button"
          onClick={handleBack}
          className="pointer-events-auto flex h-12 w-full max-w-[320px] items-center justify-center rounded-full bg-[#3F73FF] px-5 text-[15px] font-bold text-white shadow-[0_16px_42px_rgba(52,112,255,0.36)] transition active:scale-[0.985] md:h-14 md:max-w-[340px] md:text-base"
        >
          {label}
        </button>
      </div>
    </div>
  );
}
